import React, { useRef, useState, useMemo, useEffect } from "react";
import { useScreenDimensions, useDevice } from "../_layout";

import {
  Text,
  View,
  TouchableOpacity,
  PanResponder,
  Animated,
  Dimensions,
} from "react-native";
import GlobalStyles from "../../Resources/Styles";
import Colors from "../../Resources/Color";
import Fonts from "../../Resources/Fonts";
import { useTranslation } from "react-i18next";
import { Feather } from "@expo/vector-icons";

import { observer, Observer } from "mobx-react";

import ChatMediaPlayer from "./ChatMediaPlayer";
import ChatView from "./ChatView";
import CustomAlert from "../Components/CustomAlert";
import ChatManager from "../Classes/ChatManager";
import MediaManager from "../Classes/MediaManager";
import { Chat } from "../../Resources/Constants";
import AudioManager from "../Classes/AudioManager";
import ChatListView from "./ChatListView";
import ClientNameLogo from "./ClientNameLogo";
import Tooltip from "../Components/Tooltip";
import {
  ChatHoverPresentationMode,
  getChatHoverPresentationMode,
} from "./chatHoverPresentation";

const ChatHover = observer(({ coach, keyboardHeight = 0 }) => {
  const { screenSize } = useScreenDimensions();
  const { t, i18n } = useTranslation();
  const { isMobile } = useDevice();
  const chatManager = ChatManager.getInstance();
  const mediaManager = MediaManager.getInstance();
  const audioManager = AudioManager.getInstance();
  const [width, setWidth] = useState(() => Dimensions.get("window").width);
  const [hoveredButton, setHoveredButton] = useState(null);

  useEffect(() => {
    const onChange = ({ window: { width: newWidth } }) => {
      setWidth((prev) => (prev !== newWidth ? newWidth : prev));
    };
    const subscription = Dimensions.addEventListener("change", onChange);
    return () => {
      if (subscription?.remove) {
        subscription.remove();
      }
    };
  }, []);

  // 1) create a value that starts at 0
  const panX = useRef(new Animated.Value(0)).current;
  const effectiveChatIsExpanded = isMobile ? true : coach.chatIsExpanded;

  const chatWidth =
    effectiveChatIsExpanded && coach.showingChatList
      ? Chat.CHAT_WIDTH_EXPANDED
      : Chat.CHAT_WIDTH;

  // 2) hook it up to PanResponder
  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onPanResponderGrant: () => {
        // allow smooth continuation if you re‑drag
        panX.setOffset(panX.__getValue());
        panX.setValue(0);
        chatManager.setProperty("isDragging", true);
      },
      onPanResponderMove: Animated.event([null, { dx: panX }], {
        useNativeDriver: false,
      }),
      onPanResponderRelease: () => {
        // flattenOffset so panX._value becomes the final position
        chatManager.setProperty("isDragging", false);
        panX.flattenOffset();
      },
    }),
  ).current;

  const viewMargin = screenSize != "small" ? 24 : 0;
  const clampedX = useMemo(
    () =>
      width > 0
        ? Animated.diffClamp(panX, -width + chatWidth + viewMargin, viewMargin)
        : panX,
    [width, panX, viewMargin, chatWidth],
  );
  const presentationMode = getChatHoverPresentationMode({
    isMobile,
    showingChatList: coach.showingChatList,
    chatIsExpanded: effectiveChatIsExpanded,
    screenSize,
  });

  const TopBarRow = observer(() => {
    return (
      <View
        {...panResponder.panHandlers}
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          height: 52,
          borderBottomWidth: coach.selectedChatClient ? 0.1 : 0,
          borderColor: Colors.borderColor,
        }}
      >
        {coach.selectedChatClient ? (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <TouchableOpacity
              style={{
                flexDirection: "row",
                alignItems: "center",
              }}
              disabled={audioManager.isRecording}
              onPress={() => {
                if (coach.showingChatList) {
                  coach.setProperty("selectedChatId", "");
                  chatManager.setProperty("selectedReplyToId", null);
                  mediaManager.closeMediaPlayer();
                } else {
                  coach.setProperty("showingChatList", true);
                }
              }}
            >
              <Feather
                name="chevron-left"
                size={24}
                color="black"
                style={{ paddingLeft: 4 }}
              />
              <ClientNameLogo
                client={coach.selectedChatClient}
                isForChat={true}
              />
            </TouchableOpacity>
            <View>
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                <Text style={{ fontWeight: "600", fontSize: 14 }}>
                  {coach.selectedChatClient.nameForCoach}
                </Text>
                {coach.selectedClientId !== coach.selectedChatClient.id && (
                  <Tooltip
                    delayedOpen={true}
                    content="openAthlete-string"
                    placement="bottom"
                  >
                    <TouchableOpacity
                      onPress={() => {
                        coach.selectClient(
                          coach.selectedChatClient.id,
                          t,
                          i18n,
                        );
                      }}
                      onMouseEnter={() =>
                        !isMobile && setHoveredButton("openAthlete")
                      }
                      onMouseLeave={() => setHoveredButton(null)}
                      style={{ marginLeft: 4 }}
                    >
                      <Feather
                        name="arrow-up-right"
                        size={14}
                        color={
                          hoveredButton === "openAthlete"
                            ? Colors.darkgrayColor
                            : Colors.grayColor
                        }
                      />
                    </TouchableOpacity>
                  </Tooltip>
                )}
              </View>
              <Text style={{ fontSize: 10, color: Colors.grayColor }}>
                {coach.selectedChatClient.email}
              </Text>
            </View>
          </View>
        ) : (
          <TouchableOpacity
            onPress={() =>
              coach.setProperty("showingChatList", !coach.showingChatList)
            }
            style={{ flexDirection: "row", alignItems: "center" }}
          >
            <Text
              style={{
                fontWeight: "600",
                fontSize: 16,
                paddingLeft: 16,
              }}
            >
              {t("chats-string")}
            </Text>
            {coach.totalNewChats > 0 && (
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                <Text
                  style={{
                    color: Colors.bubbleColor,
                    backgroundColor: "rgba(0, 0, 0, 0.07)",
                    borderRadius: 12,
                    alignSelf: "center",
                    textAlign: "center",
                    textAlignVertical: "center",
                    fontSize: 11,
                    width: 20,
                    minWidth: 20, //To avoid being compressed when preview text is large
                    height: 20,
                    lineHeight: 20,
                    marginLeft: 8,
                    // borderColor: Colors.grayColor,
                    // borderWidth: 0.5,
                  }}
                >
                  {coach.totalNewChats <= 99 ? coach.totalNewChats : 99}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        )}

        <View style={{ flex: 1 }} />
        {!isMobile && (
          <Tooltip
            delayedOpen={true}
            content={
              coach.chatIsExpanded
                ? "minimizeChat-string"
                : "expandChat-string"
            }
            placement="bottom"
          >
            <TouchableOpacity
              onPress={() => {
                if (!coach.chatIsExpanded) {
                  coach.setProperty("chatIsExpanded", true);
                  coach.setProperty("showingChatList", true);
                } else {
                  coach.setProperty("chatIsExpanded", false);
                  coach.setProperty("showingChatList", true);
                }
              }}
            >
              <Feather
                name={coach.chatIsExpanded ? "minimize-2" : "maximize-2"}
                size={20}
                color={Colors.blackColor}
                style={{
                  paddingRight: 16,
                }}
              />
            </TouchableOpacity>
          </Tooltip>
        )}

        <Tooltip
          delayedOpen={true}
          content={
            coach.showingChatList ? "hideChat-string" : "showChat-string"
          }
          placement="bottom"
        >
          <TouchableOpacity
            onPress={() =>
              coach.setProperty("showingChatList", !coach.showingChatList)
            }
          >
            <Feather
              name={coach.showingChatList ? "chevrons-down" : "chevrons-up"}
              size={24}
              color={Colors.blackColor}
              style={{
                paddingRight: 16,
              }}
            />
          </TouchableOpacity>
        </Tooltip>
      </View>
    );
  });

  if (presentationMode === ChatHoverPresentationMode.HIDDEN_EXPANDED_DESKTOP) {
    return null;
  }

  if (presentationMode === ChatHoverPresentationMode.MOBILE_PEEK) {
    return (
      <>
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={t("showChat-string")}
          onPress={() => coach.setProperty("showingChatList", true)}
          activeOpacity={0.8}
          style={{
            position: "absolute",
            right: 0,
            bottom: keyboardHeight + 72,
            zIndex: 100,
            width: 36,
            height: 56,
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: 16,
            backgroundColor: Colors.whiteColor,
            borderWidth: 0.1,
            borderColor: Colors.borderColor,
            borderRightWidth: 0,
            alignItems: "flex-start",
            justifyContent: "center",
            paddingLeft: 7,
            shadowColor: "#000",
            shadowOpacity: 0.14,
            shadowRadius: 8,
            shadowOffset: { width: 0, height: 4 },
            elevation: 4,
          }}
        >
          <Feather name="message-circle" size={20} color={Colors.blackColor} />
          {coach.totalNewChats > 0 && (
            <View
              style={{
                position: "absolute",
                top: 7,
                left: 16,
                backgroundColor: Colors.bubbleColor,
                borderRadius: 8,
                minWidth: 15,
                height: 15,
                alignItems: "center",
                justifyContent: "center",
                paddingHorizontal: 3,
              }}
            >
              <Text
                style={{
                  color: Colors.whiteColor,
                  fontSize: 8,
                  fontWeight: "700",
                }}
              >
                {coach.totalNewChats <= 99 ? coach.totalNewChats : 99}
              </Text>
            </View>
          )}
        </TouchableOpacity>

        <Observer>
          {() =>
            chatManager.showingPermissionsAlert && (
              <CustomAlert
                isVisible={chatManager.showingPermissionsAlert}
                onClose={() =>
                  chatManager.setProperty("showingPermissionsAlert", false)
                }
                title={"permissionsAlertTitle-string"}
                text={"permissionsAlertText-string"}
                noTranslateText={true}
              />
            )
          }
        </Observer>
      </>
    );
  }

  return (
    <>
      <Animated.View
        pointerEvents="box-none"
        style={[
          {
            width: chatWidth,
            position: "absolute",
            bottom: keyboardHeight,
            right: screenSize !== "small" ? 24 : 0,
            zIndex: 100,
            maxHeight: `calc(100% - ${keyboardHeight}px)`,
            justifyContent: "flex-end",
            alignItems: "center",
            paddingTop: 8,
            maxWidth: "100%",
            transform: [{ translateX: clampedX }],
          },
        ]}
      >
        {mediaManager.showingMediaPlayer && <ChatMediaPlayer coach={coach} />}
        <View
          style={{
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            backgroundColor: "#fff",
            borderWidth: 0.1,
            borderColor: "#ccc",
            width: "100%",
            maxHeight: coach.showingChatList
              ? effectiveChatIsExpanded
                ? "100%"
                : 620
              : undefined,
            minHeight: coach.showingChatList ? 220 : 52,
            flexShrink: 100,
          }}
        >
          <TopBarRow />
          {coach.showingChatList && (
            <View style={{ flex: 1, paddingBottom: 0 }}>
              {coach.selectedChatClient ? (
                <ChatView client={coach.selectedChatClient} />
              ) : (
                <>
                  <ChatListView coach={coach} />
                </>
              )}
            </View>
          )}
        </View>
      </Animated.View>

      <Observer>
        {() =>
          chatManager.showingPermissionsAlert && (
            <CustomAlert
              isVisible={chatManager.showingPermissionsAlert}
              onClose={() =>
                chatManager.setProperty("showingPermissionsAlert", false)
              }
              title={"permissionsAlertTitle-string"}
              text={"permissionsAlertText-string"}
              noTranslateText={true}
            />
          )
        }
      </Observer>
    </>
  );
});

export default ChatHover;
